import type { UiLocale } from "./languages";
import type { MessageKey } from "./messages";

/** Only the active interface language crosses the server/client boundary. */
export type UiDictionary = {
  locale: UiLocale;
  scope: "full" | "messages";
  messages: Record<string, string>;
  source: Record<string, string>;
  editorial: Record<string, string>;
  templates: { source: string; translation: string }[];
  marketing?: { strings: Record<string, string>; words?: Record<string, string> | null; templates: { pattern: string; names: string[]; translation: string }[] } | null;
};

/** Data-free runtime: importing a UI helper must not import every language. */
export function createUiTranslator(dictionary: UiDictionary) {
  const locale = dictionary.locale;
  const lower = new Map(Object.entries(dictionary.source).map(([source, translation]) => [source.toLowerCase(), translation]));
  const marketingPatterns = dictionary.marketing?.templates.map(template => ({ ...template, regex: new RegExp(template.pattern, "u") })) ?? [];
  const marketingValues = new Map(Object.values(dictionary.marketing?.strings ?? {}).map(value => [value.replace(/\s+/gu, " ").trim(), value]));
  const patterns = dictionary.templates.map(({ source, translation }) => {
    const names: string[] = [];
    const pattern = source.replace(/\{(\w+)\}/gu, (_, name: string) => { names.push(name); return "<<<NOVILOT_PLACEHOLDER>>>"; });
    const regex = new RegExp("^" + pattern.split("<<<NOVILOT_PLACEHOLDER>>>").map(value => value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")).join("(.+?)") + "$", "u");
    return { regex, names, translation };
  }).sort((left, right) => right.regex.source.length - left.regex.source.length);
  function translateKnownFragment(value: string) {
    return dictionary.source[value] ?? lower.get(value.toLowerCase());
  }
  function translateTypedTemplate(value: string) {
    for (const pattern of patterns) {
      const match = value.match(pattern.regex);
      if (!match) continue;
      let translated = pattern.translation;
      pattern.names.forEach((name, index) => { translated = translated.replaceAll(`{${name}}`, match[index + 1] ?? `{${name}}`); });
      return translated;
    }
    return undefined;
  }

  function translateLegacyDynamic(value: string, locale: Exclude<UiLocale, "en">) {
    if (locale === "zh") {
      let match = value.match(/^Connection failed: (.+)$/u);
      if (match) return `连接失败：${match[1]}`;
      match = value.match(/^Connected as (.+)$/u);
      if (match) return `已连接为 ${match[1]}`;
      match = value.match(/^Last success: (.+)$/u);
      if (match) return `上次成功：${match[1]}`;
      match = value.match(/^(\d[\d,.]*) found$/u);
      if (match) return `找到 ${match[1]} 项`;
      match = value.match(/^Review (\d[\d,.]*)$/u);
      if (match) return `查看 ${match[1]}`;
      match = value.match(/^Opened (.+)$/u);
      if (match) return `已打开 ${match[1]}`;
      match = value.match(/^Appears in (\d[\d,.]*) chapter(s?)$/u);
      if (match) return `出现在 ${match[1]} 个章节`;
      match = value.match(/^(\d[\d,.]*)% confidence$/u);
      if (match) return `置信度 ${match[1]}%`;
      match = value.match(/^What happens in (.+)\? Set the scene, introduce a character, or pick up where you left off…$/u);
      if (match) return `${match[1]}里会发生什么？设置场景、介绍角色，或从上次停下的地方继续……`;
      match = value.match(/^Analyzes the manuscript — uses ~(.+?) \(free if unchanged since the last scan\)$/u);
      if (match) return `分析手稿——使用约 ${match[1]}（如果自上次扫描后未更改则免费）`;
      match = value.match(/^(\d[\d,.]*) credits$/u);
      if (match) return `${match[1]} 个额度`;
      match = value.match(/^(\d[\d,.]*) continuity issue(s?) — click to view$/u);
      if (match) return `${match[1]} 个连续性问题——点击查看`;
      match = value.match(/^([\d,.]+) stars?$/u);
      if (match) return `${match[1]} 星`;
      match = value.match(/^(.+): ([\d,.]+) words$/u);
      if (match) return `${match[1]}：${match[2]} 字`;
      match = value.match(/^(Dialogue|Prose): ([\d,.]+) words$/u);
      if (match) return `${translateKnownFragment(match[1]) ?? match[1]}：${match[2]} 字`;
      match = value.match(/^Chapter: (.+)$/u);
      if (match) return `章节：${match[1]}`;
      match = value.match(/^Add (.+) node$/u);
      if (match) return `添加 ${translateKnownFragment(match[1]) ?? match[1]}节点`;
      match = value.match(/^Remove from (.+)$/u);
      if (match) return `从${match[1]}中移除`;
      match = value.match(/^Delete the (.+) column$/u);
      if (match) return `删除${match[1]}列`;
      match = value.match(/^Delete (.+) and its values$/u);
      if (match) return `删除${match[1]}及其值`;
      match = value.match(/^Apply (.+)$/u);
      if (match) return `应用 ${match[1]}`;
      match = value.match(/^Delete (.+)$/u);
      if (match) return `删除 ${match[1]}`;
      match = value.match(/^Theme: (.+)$/u);
      if (match) return `主题：${match[1]}`;
      match = value.match(/^Add (.+)…$/u);
      if (match) return `添加${translateKnownFragment(match[1]) ?? match[1]}……`;
      return undefined;
    }

    if (locale === "zh-tw") {
      let match = value.match(/^Connection failed: (.+)$/u);
      if (match) return `連線失敗：${match[1]}`;
      match = value.match(/^Connected as (.+)$/u);
      if (match) return `已連線為 ${match[1]}`;
      match = value.match(/^Last success: (.+)$/u);
      if (match) return `上次成功：${match[1]}`;
      match = value.match(/^(\d[\d,.]*) found$/u);
      if (match) return `找到 ${match[1]} 項`;
      match = value.match(/^Review (\d[\d,.]*)$/u);
      if (match) return `查看 ${match[1]}`;
      match = value.match(/^Opened (.+)$/u);
      if (match) return `已開啟 ${match[1]}`;
      match = value.match(/^Appears in (\d[\d,.]*) chapter(s?)$/u);
      if (match) return `出現在 ${match[1]} 個章節`;
      match = value.match(/^(\d[\d,.]*)% confidence$/u);
      if (match) return `信心度 ${match[1]}%`;
      match = value.match(/^What happens in (.+)\? Set the scene, introduce a character, or pick up where you left off…$/u);
      if (match) return `${match[1]}裡會發生什麼？設定場景、介紹角色，或從上次停下的地方繼續……`;
      match = value.match(/^Analyzes the manuscript — uses ~(.+?) \(free if unchanged since the last scan\)$/u);
      if (match) return `分析手稿——使用約 ${match[1]}（如果自上次掃描後未更改則免費）`;
      match = value.match(/^(\d[\d,.]*) credits$/u);
      if (match) return `${match[1]} 點數`;
      match = value.match(/^(\d[\d,.]*) continuity issue(s?) — click to view$/u);
      if (match) return `${match[1]} 個連續性問題——點擊查看`;
      match = value.match(/^([\d,.]+) stars?$/u);
      if (match) return `${match[1]} 星`;
      match = value.match(/^(.+): ([\d,.]+) words$/u);
      if (match) return `${match[1]}：${match[2]} 字`;
      match = value.match(/^(Dialogue|Prose): ([\d,.]+) words$/u);
      if (match) return `${translateKnownFragment(match[1]) ?? match[1]}：${match[2]} 字`;
      match = value.match(/^Chapter: (.+)$/u);
      if (match) return `章節：${match[1]}`;
      match = value.match(/^Add (.+) node$/u);
      if (match) return `添加 ${translateKnownFragment(match[1]) ?? match[1]}節點`;
      match = value.match(/^Remove from (.+)$/u);
      if (match) return `從${match[1]}中移除`;
      match = value.match(/^Delete the (.+) column$/u);
      if (match) return `刪除${match[1]}列`;
      match = value.match(/^Delete (.+) and its values$/u);
      if (match) return `刪除${match[1]}及其值`;
      match = value.match(/^Apply (.+)$/u);
      if (match) return `應用 ${match[1]}`;
      match = value.match(/^Delete (.+)$/u);
      if (match) return `刪除 ${match[1]}`;
      match = value.match(/^Theme: (.+)$/u);
      if (match) return `主題：${match[1]}`;
      match = value.match(/^Add (.+)…$/u);
      if (match) return `添加${translateKnownFragment(match[1]) ?? match[1]}……`;
      return undefined;
    }

    if (locale === "es") {
      let match = value.match(/^Connection failed: (.+)$/u);
      if (match) return `Error de conexión: ${match[1]}`;
      match = value.match(/^Connected as (.+)$/u);
      if (match) return `Conectado como ${match[1]}`;
      match = value.match(/^Last success: (.+)$/u);
      if (match) return `Último éxito: ${match[1]}`;
      match = value.match(/^(\d[\d,.]*) found$/u);
      if (match) return `${match[1]} encontrados`;
      match = value.match(/^Review (\d[\d,.]*)$/u);
      if (match) return `Revisar ${match[1]}`;
      match = value.match(/^Opened (.+)$/u);
      if (match) return `Abierto ${match[1]}`;
      match = value.match(/^Appears in (\d[\d,.]*) chapter(s?)$/u);
      if (match) return `Aparece en ${match[1]} capítulo${match[2] ? "s" : ""}`;
      match = value.match(/^(\d[\d,.]*)% confidence$/u);
      if (match) return `${match[1]}% de confianza`;
      match = value.match(/^What happens in (.+)\? Set the scene, introduce a character, or pick up where you left off…$/u);
      if (match) return `¿Qué ocurre en ${match[1]}? Sitúa la escena, presenta a un personaje o continúa desde donde lo dejaste…`;
      match = value.match(/^(\d[\d,.]*) credits$/u);
      if (match) return `${match[1]} créditos`;
      match = value.match(/^(\d[\d,.]*) continuity issue(s?) — click to view$/u);
      if (match) return `${match[1]} problema${match[2] ? "s" : ""} de continuidad — haz clic para ver`;
      match = value.match(/^Chapter: (.+)$/u);
      if (match) return `Capítulo: ${match[1]}`;
      match = value.match(/^Delete the (.+) column$/u);
      if (match) return `Eliminar la columna ${match[1]}`;
      match = value.match(/^Delete (.+) and its values$/u);
      if (match) return `Eliminar ${match[1]} y sus valores`;
      match = value.match(/^Apply (.+)$/u);
      if (match) return `Aplicar ${match[1]}`;
      match = value.match(/^Delete (.+)$/u);
      if (match) return `Eliminar ${match[1]}`;
      return undefined;
    }

    if (locale !== "ar") return undefined;
    let match = value.match(/^Connection failed: (.+)$/u);
    if (match) return `فشل الاتصال: ${match[1]}`;
    match = value.match(/^Installed for (.+?)\. (.+)$/u);
    if (match) return `مثبّت للحساب ${match[1]}. ${translateKnownFragment(match[2]) ?? match[2]}`;
    match = value.match(/^Connected as (.+)$/u);
    if (match) return `متصل باسم ${match[1]}`;
    match = value.match(/^(Update story bible|Add to story bible|Update outline card|Add outline card|Edit|Add to canvas|Connect|Track plot thread|Update chapter|New chapter): (.+)$/u);
    if (match) {
      const label = translateKnownFragment(`${match[1]}:`);
      if (label) return `${label} ${match[2]}`;
    }
    match = value.match(/^Last success: (.+)$/u);
    if (match) return `آخر نجاح: ${match[1]}`;
    match = value.match(/^(\d[\d,.]*) found$/u);
    if (match) return `تم العثور على ${match[1]}`;
    match = value.match(/^Review (\d[\d,.]*)$/u);
    if (match) return `مراجعة ${match[1]}`;
    match = value.match(/^Opened (.+)$/u);
    if (match) return `تم فتح ${match[1]}`;
    match = value.match(/^Appears in (\d[\d,.]*) chapter(s?)$/u);
    if (match) return `يظهر في ${match[1]} ${match[1] === "1" ? "فصل" : "فصول"}`;
    match = value.match(/^(\d[\d,.]*)% confidence$/u);
    if (match) return `درجة الثقة ${match[1]}٪`;
    match = value.match(/^What happens in (.+)\? Set the scene, introduce a character, or pick up where you left off…$/u);
    if (match) return `${match[1]}؟ حدّد المشهد، وقدّم شخصية، أو واصل من حيث توقفت…`;
    match = value.match(/^Analyzes the manuscript — uses ~(.+?) \(free if unchanged since the last scan\)$/u);
    if (match) return `يحلل المخطوطة — يستخدم نحو ${match[1]} (مجانًا إذا لم تتغير منذ الفحص الأخير)`;
    match = value.match(/^Out of credits\. They refill (.+)\. Your writing and saved results are unaffected\. Tap to see how credits work\.$/u);
    if (match) return `نفدت الأرصدة. تتجدد في ${match[1]}. لن تتأثر كتابتك ونتائجك المحفوظة. انقر لمعرفة كيفية عمل الأرصدة.`;
    match = value.match(/^(\d[\d,.]*) of (\d[\d,.]*) credits left, refilling (.+)\. Tap to see what used them and how credits work\.$/u);
    if (match) return `${match[1]} من أصل ${match[2]} رصيد متبقٍ، يتجدد في ${match[3]}. انقر لمعرفة ما استهلكه وكيفية عمل الأرصدة.`;
    match = value.match(/^Out of credits, back (.+)$/u);
    if (match) return `نفدت الأرصدة، تعود في ${match[1]}`;
    match = value.match(/^(\d[\d,.]*) credits, refill (.+)$/u);
    if (match) return `${match[1]} رصيد، يتجدد في ${match[2]}`;
    match = value.match(/^(\d[\d,.]*) credits$/u);
    if (match) return `${match[1]} رصيد`;
    match = value.match(/^Status: (.+) — click to (change|cycle)$/u);
    if (match) {
      const action = match[2] === "change" ? "انقر للتغيير" : "انقر للتبديل";
      return `الحالة: ${translateKnownFragment(match[1]) ?? match[1]} — ${action}`;
    }
    match = value.match(/^(\d[\d,.]*) continuity issue(s?) — click to view$/u);
    if (match) return `${match[1]} ${match[1] === "1" ? "مشكلة استمرارية" : "مشكلات استمرارية"} — انقر للعرض`;
    match = value.match(/^([\d,.]+) stars?$/u);
    if (match) {
      const count = Number(match[1]);
      return `${match[1]} ${count === 1 ? "نجمة" : count === 2 ? "نجمتان" : "نجوم"}`;
    }
    match = value.match(/^(.+): ([\d,.]+) words$/u);
    if (match) return `${match[1]}: ${match[2]} كلمة`;
    match = value.match(/^(Dialogue|Prose): ([\d,.]+) words$/u);
    if (match) return `${translateKnownFragment(match[1]) ?? match[1]}: ${match[2]} كلمة`;
    match = value.match(/^(.+): rhythm ([\d,.]+)\/10$/u);
    if (match) return `${match[1]}: الإيقاع ${match[2]}/10`;
    match = value.match(/^(.+): ([\d,.]+) out of 10$/u);
    if (match) return `${match[1]}: ${match[2]} من 10`;
    match = value.match(/^(.+): (\d[\d,.]*)%$/u);
    if (match) return `${match[1]}: ${match[2]}٪`;
    match = value.match(/^Chapter: (.+)$/u);
    if (match) return `الفصل: ${match[1]}`;
    match = value.match(/^Add (.+) node$/u);
    if (match) return `إضافة عقدة ${translateKnownFragment(match[1]) ?? match[1]}`;
    match = value.match(/^Remove from (.+)$/u);
    if (match) return `إزالة من ${match[1]}`;
    match = value.match(/^Mark as advancing (.+)$/u);
    if (match) return `تحديد ${match[1]} كمسار متقدم`;
    match = value.match(/^Delete the (.+) column$/u);
    if (match) return `حذف عمود ${match[1]}`;
    match = value.match(/^Delete (.+) and its values$/u);
    if (match) return `حذف ${match[1]} وقيمه`;
    match = value.match(/^Apply (.+)$/u);
    if (match) return `تطبيق ${match[1]}`;
    match = value.match(/^Overwrite (.+) with current query$/u);
    if (match) return `استبدال ${match[1]} بالاستعلام الحالي`;
    match = value.match(/^Delete (.+)$/u);
    if (match) return `حذف ${match[1]}`;
    match = value.match(/^Generate (.+) with AI — 1 credit$/u);
    if (match) return `إنشاء ${translateKnownFragment(match[1]) ?? match[1]} بالذكاء الاصطناعي — رصيد واحد`;
    match = value.match(/^Theme: (.+)$/u);
    if (match) return `السمة: ${match[1]}`;
    match = value.match(/^Add (.+)…$/u);
    if (match) return `إضافة ${translateKnownFragment(match[1]) ?? match[1]}…`;
    match = value.match(/^(.+): ([^:]+)$/u);
    if (match) {
      const left = translateKnownFragment(match[1]);
      const right = translateKnownFragment(match[2]);
      if (left || right) return `${left ?? match[1]}: ${right ?? match[2]}`;
    }
    return undefined;
  }


  function translateSource(value: string): string | undefined {
    if (locale === "en") return undefined;
    const normalized = value.replace(/\s+/gu, " ").trim();
    const editorial = dictionary.editorial[normalized];
    if (editorial) return editorial;
    const catalog = dictionary.source;
    const exact = translateKnownFragment(normalized);
    if (exact) return exact;

    const typed = translateTypedTemplate(normalized);
    if (typed) return typed;

    if (locale === "ar") {
      const credits = normalized.match(/^(\d[\d,.]*)\s+credits?$/u);
      if (credits) return `${credits[1]} ${credits[0].endsWith("credits") ? "رصيدًا" : "رصيد"}`;
      const done = normalized.match(/^(\d+\/\d+)\s+done$/u);
      if (done) return `${done[1]} مكتمل`;
      const wordsPerDay = normalized.match(/^(~?[\d,.]+)\s+words\/day\s+avg$/u);
      if (wordsPerDay) return `${wordsPerDay[1]} ${catalog["words/day avg"] ?? "متوسط الكلمات/اليوم"}`;
      const minutes = normalized.match(/^(~?[\d,.]+)\s+min read$/u);
      if (minutes) return `${minutes[1]} ${catalog["min read"] ?? "دقيقة قراءة"}`;
      const dayWords = normalized.match(/^(Sun|Mon|Tue|Wed|Thu|Fri|Sat):\s*([\d,.]+)\s+words$/u);
      if (dayWords) return `${catalog[dayWords[1]] ?? dayWords[1]}: ${dayWords[2]} كلمة`;
      const scenes = normalized.match(/^(\d[\d,.]*)\s+scenes · Drag to reorder · Click any field to edit$/u);
      if (scenes) return `${scenes[1]} مشاهد · اسحب لإعادة الترتيب · انقر على أي حقل لتعديله`;
      const chapters = normalized.match(/^(\d[\d,.]*)\s+chapters ·\s*(\d[\d,.]*)\s+words$/u);
      if (chapters) return `${chapters[1]} ${chapters[1] === "1" ? "فصل" : "فصول"} · ${chapters[2]} كلمة`;
    }

    return translateLegacyDynamic(normalized, locale);
  }

  return {
    translateSource,
    translateMarketing: (value: string) => {
      if (!dictionary.marketing) return value;
      const normalized = value.replace(/\s+/gu, " ").trim();
      const exact = dictionary.marketing.strings[value] ?? dictionary.marketing.strings[normalized];
      if (exact) return exact;
      const translatedValue = marketingValues.get(normalized);
      if (translatedValue) return translatedValue;
      for (const pattern of marketingPatterns) {
        const match = normalized.match(pattern.regex);
        if (!match) continue;
        let translated = pattern.translation;
        pattern.names.forEach((name, index) => { translated = translated.replaceAll(`{${name}}`, match[index + 1] ?? `{${name}}`); });
        return translated;
      }
      if (dictionary.marketing.words) {
        const words = dictionary.marketing.words;
        const generated = normalized.replace(/[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ'-]*/gu, word => {
          const translation = words[word.toLowerCase()];
          if (!translation) return word;
          return word[0] === word[0].toUpperCase() ? translation.charAt(0).toUpperCase() + translation.slice(1) : translation;
        });
        if (generated !== normalized) return generated;
      }
      return value;
    },
    t: (key: MessageKey, values: Record<string, string | number> = {}) =>
      (dictionary.messages[key] ?? key).replace(/\{(\w+)\}/g, (_, name: string) => String(values[name] ?? `{${name}}`)),
  };
}
